import {Component, inject, Injector, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {routesNames} from "@app/app-routing.module";
import {PopupDispatcher} from "@app/presentation/components/errors/popup.dispatcher";
import {LoginViewModel} from "@app/presentation/pages/login/login.view-model";
import {environment} from 'src/environments/environment';
import {LocalizedRouter} from "@app/presentation/services/localized-router";
import {DeliveryPoint} from "@app/core/delivery-point/application/dtos/delivery-point";
import {DeliveryPointService} from "@app/presentation/services/delivery-point/delivery-point.service";
import {Credentials} from "@app/core/credentials/domain/credentials";
import {ActivatedRoute} from "@angular/router";
import {SIDE_SHEET_DATA} from "@app/presentation/services/side-sheet/side-sheet.service";
import {BehaviorSubject} from "rxjs";
import {map} from "rxjs/operators";
import {ExitService} from "@app/presentation/services/exit.service";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [  LoginViewModel]
})
export class LoginComponent implements OnInit {
  routeNames = routesNames

  loginForm = new UntypedFormGroup({
    email: new UntypedFormControl(environment.DEFAULT_EMAIL, [Validators.required, Validators.email]),
    password: new UntypedFormControl(environment.DEFAULT_PASSWORD, Validators.required),
    promoter: new UntypedFormControl(null),
  });

  isLoginLoading$ = this.loginViewModel.isLoginLoading$;

  private readonly USER_QUERY_KEY = 'user';
  private readonly PASSWORD_QUERY_KEY = 'pwd';
  private readonly PROMOTER_QUERY_KEY = 'promoter';

  private readonly isPasswordEncryptedSubject = new BehaviorSubject<boolean>(false);
  private readonly isPasswordEncrypted$ = this.isPasswordEncryptedSubject.asObservable();
  readonly canDisplayPassword$ = this.isPasswordEncrypted$.pipe(map(isPasswordEncrypted => !isPasswordEncrypted));

  constructor(
    private loginViewModel: LoginViewModel,
    private popupDispatcher: PopupDispatcher,
    private localizedRouter: LocalizedRouter,
    private deliveryPointService: DeliveryPointService,
    private activatedRoute: ActivatedRoute,
    private injector: Injector,
  ) { }

  ngOnInit(): void {
    this.popupDispatcher.setNotifier(this.loginViewModel.notifier);
    this.populateLoginForm();
  }

  /**
   * Populate the login form with the email and password from the query params.
   * Examples:
   * - http://localhost:4200/inicio?user=amorenotynhg@homebakery.es&pwd=535087639979d70a25f4be44a8375718e0b6e0169df2d5fbf91fa1b46b4b9a83&promoter=DIGITALBUSINESS
   *
   */
  async populateLoginForm() {
    let user: string | null = this.activatedRoute.snapshot.queryParams[this.USER_QUERY_KEY] ?? null; //If the value is undefined, it will be null.
    let password: string | null = this.activatedRoute.snapshot.queryParams[this.PASSWORD_QUERY_KEY] ?? null;
    let promoter: string = this.activatedRoute.snapshot.queryParams[this.PROMOTER_QUERY_KEY] ?? null;

    try {
      //Get the user and password from the side sheet data if it exists
      const sideSheetData = inject(SIDE_SHEET_DATA);
      user = sideSheetData.queryParams.user ?? null;
      password = sideSheetData.queryParams.pwd ?? null;
      promoter = sideSheetData.queryParams.promoter ?? null;
    } catch (e) {
    }

    if (user !== null) {
      const emailControl = this.loginForm.controls.email;
      emailControl.setValue(user);
    }
    if (promoter !== null) {
      const promoterControl = this.loginForm.controls.promoter;
      promoterControl.setValue(promoter);
    }
    if (password !== null) {
      const passwordControl = this.loginForm.controls.password;
      this.isPasswordEncryptedSubject.next(true);
      passwordControl.setValue(password);
    }
  }

  async logOut() {
    await this.loginViewModel.logOut();
  }

  async onSubmit(): Promise<void> {
    // Log out when user submits to delete old previous data, neede in login with url case
    await this.logOut();

    if (this.isPasswordEncryptedSubject.value) {
      this.loginWithEncryptedPassword();
    } else {
      this.login();
    }
  }

  private login() {
    const values = this.loginForm.value;
    this.loginViewModel.login(
        new Credentials(values.email, values.password),
        (deliveryPoints: DeliveryPoint[]) => this.selectDeliveryPoint(deliveryPoints),
        values.promoter ?? null,
    ).then(() => this.exit())
  }

  private loginWithEncryptedPassword() {
    const values = this.loginForm.value;
    this.loginViewModel.loginWithEncryptedPassword(
        new Credentials(values.email, values.password),
        (deliveryPoints: DeliveryPoint[]) => this.selectDeliveryPoint(deliveryPoints),
        values.promoter?? null,
    ).then(() =>
        this.exit())
  }

  private async selectDeliveryPoint(deliveryPoints: DeliveryPoint[]): Promise<DeliveryPoint | null> {
    return this.deliveryPointService.selectDeliveryPoint(deliveryPoints);
  }

  private exit() {
    if (ExitService.hasReturnUrl(this.injector)) {
      ExitService.navigateToReturnUrl(this.injector);
      return;
    }
    this.localizedRouter.navigate(['/', routesNames.home]);
  }
}
